<template>
  <div>
    <atomic-hidden-title
      :current-locale-data="sportsbookPageContent?.currentLocaleData"
      :default-locale-data="sportsbookPageContent?.defaultLocaleData"
      content-path="IPageMeta.title"
    />
    <atomic-sport-preloader ref="preloader" />
    <div class="betting-altenar">
      <div
        id="betting-container"
        class="container"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import type { ISportsbookPage } from '~/types';
import type { IWidgetParams } from '@skeleton/stores/useAltenarStore';
import { ModalName, MODALS_URL } from '@skeleton/consts/modals';

const { closeModal } = useModalStore();

const deviceStore = useDeviceStore();
const { isMobile } = storeToRefs(deviceStore);

const altenarStore = useAltenarStore();
const {
  addSportBookWidget,
  removeSportBookWidget,
  updateSportBookWidget,
  getCategoryInfo,
  getChampInfo,
  sportIdByName,
  specialPages,
} = altenarStore;

const { getContentData } = useContentLogic<ISportsbookPage>({
  contentKey: 'sportsbookPageContent',
  contentRoute: ['pages', 'sportsbook'],
  isPage: true,
});

const { data: sportsbookPageContent } = await useLazyAsyncData(getContentData);

const preloader = ref(null);
const route = useRoute();
const router = useRouter();

const getCategoryIds = async (sportId: number | undefined, categoryName: string) => {
  const { query } = useRoute();
  const queryCategoryIds = query.categoryIds?.split(',').map(Number);

  if (queryCategoryIds) {
    return queryCategoryIds;
  }

  if (categoryName && sportId) {
    const category = await getCategoryInfo(sportId, categoryName, false);
    return category ? [category] : undefined;
  }

  return undefined;
};

const getChampionshipIds = async (sportId: number | undefined, categoryId: number | undefined, champName: string) => {
  const { query } = useRoute();
  const queryChampionshipIds = query.championshipIds?.split(',').map(Number);

  if (queryChampionshipIds) {
    return queryChampionshipIds;
  }

  if (sportId && categoryId && champName) {
    const championship = await getChampInfo(sportId, categoryId, champName, false);
    return championship ? [championship] : undefined;
  }

  return undefined;
};

const parseURL = async (slug: string[] | string): Promise<Maybe<IWidgetParams>> => {
  const { query } = useRoute();
  if (typeof slug === 'string') {
    slug = [slug];
  }

  let page = 'overview';
  const [sport, category, champ, event] = slug;
  let sportId,
    categoryIds,
    championshipIds = undefined;

  const eventId = event
    ? Number.parseInt(event)
    : query.eventId
      ? Number.parseInt(query.eventId as string)
      : undefined;

  if (specialPages.includes(sport)) {
    page = sport;
    if (category) {
      sportId = sportIdByName(category);
    }
  }
  else {
    sportId = sportIdByName(sport);
  }

  if (sportId) {
    categoryIds = await getCategoryIds(sportId, category);
    championshipIds = await getChampionshipIds(sportId, categoryIds?.[0], champ);
  }

  if (!specialPages.includes(sport)) {
    if (eventId) {
      page = 'event';
    }
    else if (championshipIds) {
      page = 'championship';
    }
    else if (categoryIds) {
      page = 'category';
    }
    else if (sportId) {
      page = 'sport';
    }
  }

  return {
    page,
    ...(sportId !== undefined && { sportId }),
    ...(categoryIds !== undefined && categoryIds.length > 0 && { categoryIds }),
    ...(championshipIds !== undefined && championshipIds.length > 0 && { championshipIds }),
    ...(eventId !== undefined && { eventId }),
  };
};

const initAltenarSportBook = async () => {
  const slug = route.params.slug as string[];
  const widgetParams: Maybe<IWidgetParams> = await parseURL(slug);

  if (widgetParams.sportId) {
    const { getSportInfo } = useCoreGamesApi();
    await getSportInfo(widgetParams.sportId);
  }

  await addSportBookWidget('betting-container', widgetParams).then(() => {
    preloader.value?.handleSBLoaded();
  });
};

const layoutStore = useLayoutStore();
const { compactDrawer } = layoutStore;

onMounted(async () => {
  compactDrawer(true, false);
  await initAltenarSportBook();

  if (isMobile.value) {
    const seoTextBlock: HTMLElement | null = document.querySelector('.text-wrap');
    if (seoTextBlock) seoTextBlock.style.display = 'none';
  }

  router.afterEach(async (to, from) => {
    if (!from.fullPath.includes('/sport') && to.fullPath.includes('/sport')) {
      const widgetParams: Maybe<IWidgetParams> = await parseURL(to.params.slug);
      updateSportBookWidget(widgetParams);
      closeModal(ModalName.GLOBAL_SEARCH);
    }

    return true;
  });

  router.beforeEach(async (to, from) => {
    const toHasModalUrl = Object.values(MODALS_URL).some(modalUrl => to.fullPath.includes(`${modalUrl}=true`));

    const fromHasModalUrl = Object.values(MODALS_URL).some(modalUrl => from.fullPath.includes(`${modalUrl}=true`));

    if (from.fullPath.includes('/sport') && !to.fullPath.includes('/sport')) {
      await removeSportBookWidget();
    }

    if (from.fullPath.includes('/sport') && to.fullPath.includes('/sport') && !toHasModalUrl && !fromHasModalUrl) {
      const widgetParams: Maybe<IWidgetParams> = await parseURL(to.params.slug);
      updateSportBookWidget(widgetParams);
      closeModal(ModalName.GLOBAL_SEARCH);
      return false;
    }

    return true;
  });
});

onBeforeUnmount(async () => {
  const seoTextBlock: any = document.querySelector('.text-wrap');
  if (seoTextBlock) seoTextBlock.style.display = null;

  const storageDrawerCompact = localStorage.getItem('IS_DRAWER_COMPACT') === 'true';
  compactDrawer(storageDrawerCompact, false);
});
</script>

<style src="~/assets/styles/pages/sport.scss" lang="scss" />
